/**
 * ErrorBoundary — bir alt ağaçta oluşan render hatalarını yakalar ve
 * tüm sayfanın beyaza dönmesini engeller. Kullanıcıya kurtarma seçenekleri
 * (Tekrar dene / Sayfayı yenile / Ana sayfa) sunar.
 *
 * İki seviyede kullanılır:
 *   - App genelinde (son çare)
 *   - Her rota içeriğinde (bir ekran çökse bile üst navigasyon ayakta kalır)
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Prod'da sessiz; geliştirmede konsola yaz. (İleride Sentry vb. buraya.)
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const label = this.props.label || 'Dieser Bereich';

    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-1">
            Etwas ist schiefgelaufen
          </h1>
          <p className="text-sm text-slate-600 mb-1">
            {label} konnte nicht geladen werden. Ihre Daten sind sicher.
          </p>
          <p className="text-xs text-slate-400 mb-6">
            Versuchen Sie es erneut oder laden Sie die Seite neu.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Erneut versuchen
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Seite neu laden
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <Home className="w-4 h-4" /> Startseite
            </button>
          </div>

          {import.meta.env?.DEV && this.state.error && (
            <pre className="mt-6 text-left text-[10px] text-red-500 bg-red-50 p-3 rounded-lg overflow-auto max-h-40">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
